import { useEffect, useRef } from "react";

// Replaces the mouse cursor with a small gold sparkle that streaks like a
// falling star and sheds fine stardust. Disabled on touch devices and for
// users who prefer reduced motion (the native cursor stays in those cases).

type Particle = {
  x: number; y: number;
  vx: number; vy: number;
  born: number; life: number;
  size: number; phase: number;
  sprite: HTMLCanvasElement;
  sparkle: boolean; rot: number; spin: number;
};

type TrailPoint = { x: number; y: number; t: number };

// Mostly golds from the site palette, with an occasional soft blue glint
const DUST_COLORS = ["#C0870C", "#DDA947", "#DDA947", "#E3B85B", "#E3B85B", "#FFEDBE", "#4AB1D6"];
const STAR_COLOR = "#DDA947";
const CORE_COLOR = "#FFFAEB";

const TRAIL_MS = 150;          // how long the falling-star tail lingers
const SPAWN_EVERY_PX = 7;      // distance travelled per stardust particle
const MAX_PARTICLES = 140;
const GRAVITY = 0.00005;       // px / ms^2, a slow downward drift
const IDLE_STOP_MS = 1500;     // stop animating this long after the last movement

const rgba = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

// Pre-rendered soft glow dot, scaled per particle with drawImage
const makeGlow = (hex: string) => {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, rgba(hex, 1));
  grad.addColorStop(0.18, rgba(hex, 0.85));
  grad.addColorStop(0.45, rgba(hex, 0.2));
  grad.addColorStop(1, rgba(hex, 0));
  g.fillStyle = grad;
  g.fillRect(0, 0, 32, 32);
  return c;
};

// Four-point sparkle with concave sides
const sparklePath = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number) => {
  const pinch = r * 0.14;
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const a = rot + (i * Math.PI) / 2;
    const tip = [x + Math.cos(a) * r, y + Math.sin(a) * r];
    const mid = a + Math.PI / 4;
    const ctrl = [x + Math.cos(mid) * pinch, y + Math.sin(mid) * pinch];
    if (i === 0) ctx.moveTo(tip[0], tip[1]);
    const next = a + Math.PI / 2;
    ctx.quadraticCurveTo(ctrl[0], ctrl[1], x + Math.cos(next) * r, y + Math.sin(next) * r);
  }
  ctx.closePath();
};

const StardustCursor = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canFancy =
      window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    if (!canFancy || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    document.documentElement.classList.add("stardust-cursor");

    const sprites = DUST_COLORS.map(makeGlow);
    const halo = makeGlow(STAR_COLOR);
    const particles: Particle[] = [];
    const trail: TrailPoint[] = [];

    let w = 0, h = 0;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    let x = -100, y = -100;
    let visible = false;
    let lastMove = 0;
    let lastT = performance.now();
    let spawnAcc = 0;
    let hoverTarget = 1;   // star grows slightly over links and buttons
    let scale = 1;
    let raf = 0;

    const spawn = (px: number, py: number, mvx: number, mvy: number, burst = false) => {
      if (particles.length >= MAX_PARTICLES) particles.shift();
      const sparkle = Math.random() < (burst ? 0.35 : 0.18);
      const spread = burst ? 0.08 : 0.025;
      const ang = Math.random() * Math.PI * 2;
      particles.push({
        x: px + (Math.random() - 0.5) * 6,
        y: py + (Math.random() - 0.5) * 6,
        vx: Math.cos(ang) * spread * Math.random() - mvx * 0.04,
        vy: Math.sin(ang) * spread * Math.random() - mvy * 0.04 + 0.01,
        born: performance.now(),
        life: 700 + Math.random() * 800,
        size: sparkle ? 2.2 + Math.random() * 1.8 : 0.6 + Math.random() * 1.4,
        phase: Math.random() * Math.PI * 2,
        sprite: sprites[Math.floor(Math.random() * sprites.length)],
        sparkle,
        rot: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.002,
      });
    };

    const frame = (now: number) => {
      const dt = Math.min(now - lastT, 50);
      lastT = now;
      ctx.clearRect(0, 0, w, h);

      // stardust
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        const age = now - p.born;
        if (age > p.life) { particles.splice(i, 1); continue; }
        p.vy += GRAVITY * dt;
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.spin * dt;
        const f = age / p.life;
        const twinkle = 0.65 + 0.35 * Math.sin(age * 0.018 + p.phase);
        ctx.globalAlpha = Math.pow(1 - f, 1.4) * twinkle;
        const s = p.size * (1 - f * 0.4);
        if (p.sparkle) {
          ctx.drawImage(p.sprite, p.x - s * 2.5, p.y - s * 2.5, s * 5, s * 5);
          sparklePath(ctx, p.x, p.y, s * 1.6, p.rot);
          ctx.fillStyle = STAR_COLOR;
          ctx.fill();
        } else {
          ctx.drawImage(p.sprite, p.x - s * 3, p.y - s * 3, s * 6, s * 6);
        }
      }

      // falling-star tail
      while (trail.length && now - trail[0].t > TRAIL_MS) trail.shift();
      ctx.lineCap = "round";
      for (let i = 1; i < trail.length; i++) {
        const f = (now - trail[i].t) / TRAIL_MS;
        ctx.globalAlpha = 0.5 * (1 - f);
        ctx.strokeStyle = STAR_COLOR;
        ctx.lineWidth = 2.6 * (1 - f) + 0.3;
        ctx.beginPath();
        ctx.moveTo(trail[i - 1].x, trail[i - 1].y);
        ctx.lineTo(trail[i].x, trail[i].y);
        ctx.stroke();
      }
      if (trail.length) {
        const last = trail[trail.length - 1];
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      // the star itself
      scale += (hoverTarget - scale) * Math.min(1, dt * 0.015);
      if (visible) {
        const breathe = 1 + 0.06 * Math.sin(now * 0.004);
        const r = 9 * scale * breathe;
        ctx.globalAlpha = 0.45;
        ctx.drawImage(halo, x - r * 1.8, y - r * 1.8, r * 3.6, r * 3.6);
        ctx.globalAlpha = 0.55;
        sparklePath(ctx, x, y, r * 0.55, Math.PI / 4);
        ctx.fillStyle = STAR_COLOR;
        ctx.fill();
        ctx.globalAlpha = 1;
        sparklePath(ctx, x, y, r, 0);
        ctx.fill();
        sparklePath(ctx, x, y, r * 0.42, 0);
        ctx.fillStyle = CORE_COLOR;
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const busy = particles.length || trail.length || now - lastMove < IDLE_STOP_MS || Math.abs(hoverTarget - scale) > 0.01;
      raf = busy ? requestAnimationFrame(frame) : 0;
    };

    const wake = () => {
      if (!raf) {
        lastT = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const now = performance.now();
      const nx = e.clientX, ny = e.clientY;
      const mvx = visible ? nx - x : 0, mvy = visible ? ny - y : 0;
      const dist = Math.hypot(mvx, mvy);
      spawnAcc += dist;
      let n = 0;
      while (spawnAcc >= SPAWN_EVERY_PX && n < 6) {
        const t = Math.random();
        spawn(x + mvx * t, y + mvy * t, mvx / 16, mvy / 16);
        spawnAcc -= SPAWN_EVERY_PX;
        n++;
      }
      if (n === 6) spawnAcc = 0;
      x = nx; y = ny;
      visible = true;
      lastMove = now;
      trail.push({ x, y, t: now });
      const el = e.target as Element | null;
      hoverTarget = el?.closest?.("a, button, [role='button'], input, select, textarea, label") ? 1.35 : 1;
      wake();
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      for (let i = 0; i < 12; i++) spawn(e.clientX, e.clientY, 0, 0, true);
      wake();
    };

    const onLeave = (e: MouseEvent) => {
      if (!e.relatedTarget) { visible = false; trail.length = 0; wake(); }
    };

    // resizing clears the canvas, so redraw even if the animation is idle
    const onResize = () => { resize(); wake(); };

    window.addEventListener("resize", onResize);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    document.addEventListener("mouseout", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      document.documentElement.classList.remove("stardust-cursor");
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      document.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 9999 }}
    />
  );
};

export default StardustCursor;
